import { useState } from 'react';
import useSWR from 'swr';
import axiosInstance from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Loader2, Plus, NotebookPen, Pencil } from 'lucide-react';
import type { Member, MemberNote } from '@/types';
import { format } from 'date-fns';

interface MemberNoteDialogProps {
  member: Member;
  communityId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function MemberNoteDialog({ member, communityId, isOpen, onClose }: MemberNoteDialogProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editText, setEditText] = useState('');

  const {
    data: notes,
    isLoading,
    mutate,
  } = useSWR<MemberNote[]>(
    isOpen ? `/api/communities/${communityId}/notes/${member.id}` : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data)
  );

  const handleSubmit = async () => {
    if (!title.trim() || !text.trim()) {
      toast.error('Title and text are required');
      return;
    }

    try {
      setIsSubmitting(true);
      await axiosInstance.post(`/api/communities/${communityId}/notes`, {
        memberId: member.id,
        title,
        text,
      });
      toast.success('Note added successfully');
      setIsAdding(false);
      setTitle('');
      setText('');
      mutate();
    } catch (error) {
      console.error(error);
      toast.error('Failed to add note');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (noteId: string) => {
    if (!editTitle.trim() || !editText.trim()) {
      toast.error('Title and text are required');
      return;
    }

    try {
      setIsSubmitting(true);
      await axiosInstance.patch(`/api/communities/${communityId}/notes`, {
        noteId,
        title: editTitle,
        text: editText,
      });
      toast.success('Note updated successfully');
      setEditingNoteId(null);
      mutate();
    } catch (error) {
      console.error(error);
      toast.error('Failed to update note');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Notes for {member.name}</SheetTitle>
          <SheetDescription>Notes you make are only visible to you</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {!isAdding ? (
            <Button onClick={() => setIsAdding(true)} className="w-full gap-2">
              <Plus className="h-4 w-4" /> Add New Note
            </Button>
          ) : (
            <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
              <h4 className="font-medium text-sm">New Note</h4>
              <div className="space-y-2">
                <Input
                  placeholder="Note Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Textarea
                  placeholder="Note content..."
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={4}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setIsAdding(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button onClick={handleSubmit} disabled={isSubmitting}>
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Note'}
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <h4 className="font-semibold flex items-center gap-2">
              <NotebookPen className="h-4 w-4" /> Previous Notes
            </h4>

            {isLoading ? (
              <div className="flex justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : notes?.length === 0 ? (
              <p className="text-sm text-muted-foreground italic text-center p-8 border border-dashed rounded-lg">
                No notes found for {member.name}.
              </p>
            ) : (
              <div className="space-y-4">
                {notes?.map((note) =>
                  editingNoteId === note.id ? (
                    <div key={note.id} className="border rounded-lg p-4 space-y-4 bg-muted/30">
                      <h4 className="font-medium text-sm">Edit Note</h4>
                      <div className="space-y-2">
                        <Input
                          placeholder="Note Title"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Textarea
                          placeholder="Note content..."
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          rows={4}
                        />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingNoteId(null)}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleUpdate(note.id)}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      key={note.id}
                      className="border rounded-lg p-4 space-y-2 bg-card group relative"
                    >
                      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 bg-background/80 hover:bg-muted shadow-sm border"
                          onClick={() => {
                            setEditingNoteId(note.id);
                            setEditTitle(note.title);
                            setEditText(note.text);
                          }}
                        >
                          <Pencil className="h-3 w-3 text-muted-foreground" />
                        </Button>
                      </div>

                      <div className="flex flex-col gap-1 pr-10">
                        <h5 className="font-medium leading-tight">{note.title}</h5>
                        {note.communityName && (
                          <div className="flex items-center">
                            <span className="text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded-md inline-block font-medium">
                              Created in {note.communityName}
                            </span>
                          </div>
                        )}
                        <span className="text-[11px] text-muted-foreground mt-1">
                          {format(
                            new Date(
                              note.updatedAt > note.createdAt ? note.updatedAt : note.createdAt
                            ),
                            'MMM d, yyyy h:mm a'
                          )}
                          {note.updatedAt > note.createdAt && ' (edited)'}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap text-muted-foreground mt-2">
                        {note.text}
                      </p>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
